import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useNavigate, useLocation } from 'react-router-dom';
import Header from '../../components/HeaderAdmin';
import './AddUser.css';
import { FAMILY_USERS, MLT_USERS, USERS_BASE_URL, USER_TYPES_URL } from '../ApiDetails.js';
import { PillsInput, Pill, Combobox, useCombobox } from '@mantine/core';

function AddUser() {
  const [UserName, setUsername] = useState('');
  const [FirstName, setFirstName] = useState('');
  const [LastName, setLastName] = useState('');
  const [Email, setEmail] = useState('');
  const [Location, setLocation] = useState('');
  const [selectedRoles, setSelectedRoles] = useState([]);
  const [userTypes, setUserTypes] = useState([]);
  const [userId, setUserId] = useState(null);

  const [familyUsers, setFamilyUsers] = useState([]);
  const [MLTUsers, setMLTUsers] = useState([]);
  const [selectedCoordinatorIds, setSelectedCoordinatorIds] = useState([]);
  const [hoveredRole, setHoveredRole] = useState(null);
  const [familyCoordinatorRoleId, setFamilyCoordinatorRoleId] = useState(null);
  const [mltCoordinatorRoleId, setMltCoordinatorRoleId] = useState(null);
  const [isDisabled, setIsDisabled] = useState(null);
  const [MarsChampion, setMarsChampion] = useState(null);

  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const [search, setSearch] = useState('');
  const [options, setOptions] = useState([]);
  const [values, setValues] = useState([]);

  const handleValueSelect = (user) => {
    setSelectedCoordinatorIds((current) => current.includes(user.id) ? current.filter((v) => v !== user.id) : [...current, user.id]);
    setSearch("");
  }

  const handleValueRemove = (user) => {
    setSelectedCoordinatorIds((current) => current.filter((v) => v !== user.id));
  }

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const { user } = location.state;
    setUserId(user.id);
    setUsername(user.userName);
    setFirstName(user.firstName);
    setLastName(user.lastName);
    setEmail(user.email);
    setLocation(user.location);
    setIsDisabled(user.isDisabled);
    setMarsChampion(user.marsChampion)

    // Map user type names to their respective IDs
    const userTypeNames = user.userTypenames;
    const selectedRoleIds = userTypes
      .filter((userType) => userTypeNames.includes(userType.type))
      .map((userType) => userType.id);

    setSelectedRoles(selectedRoleIds);

    const coordinatorIds = user.userLookups
      ?.filter(lookup => lookup.typeID === familyCoordinatorRoleId || lookup.typeID === mltCoordinatorRoleId)
      ?.flatMap(u => u.coOrdinatorId ? u.coOrdinatorId.split(',').map(Number) : []);

    setSelectedCoordinatorIds(coordinatorIds);
  }, [location.state, userTypes]);

  useEffect(() => {
    fetchUserTypes();
    fetchFamilyUsers();
    fetchMLTusers();
  }, []);

  useEffect(() => {
    const users = selectedRoles.includes(familyCoordinatorRoleId) ? familyUsers : MLTUsers;
    setOptions(users?.length > 0 ? users
      .filter(user => !selectedCoordinatorIds.includes(user.id) && user.fullname.toLowerCase().includes(search.trim().toLowerCase()))
      .map((user) => (
        <Combobox.Option value={user} key={user.id} active={selectedCoordinatorIds.includes(user.id)}>
          {user.fullname}
        </Combobox.Option>
      )) : []);

    setValues(users
      .filter(user => selectedCoordinatorIds.includes(user.id))
      .map((user) => (
        <Pill key={user.id} withRemoveButton onRemove={() => handleValueRemove(user)}>
          {user.fullname}
        </Pill>
      )));
  }, [familyUsers, MLTUsers, selectedRoles, selectedCoordinatorIds, search])

  const fetchUserTypes = async () => {
    try {
      const response = await axios.get(USER_TYPES_URL);
      const data = response.data.$values || response.data;
      const roles = Array.isArray(data) ? data : [];
      setUserTypes(roles);
      setFamilyCoordinatorRoleId(roles.find(v => v.type === "FamilyCoordinator")?.id || null);
      setMltCoordinatorRoleId(roles.find(v => v.type === "MLTCoordinator")?.id || null);
    } catch (error) {
      console.error('Error fetching user types:', error);
    }
  };

  const fetchFamilyUsers = async () => {
    try {
      const response = await axios.get(FAMILY_USERS);
      setFamilyUsers(response.data);
    } catch (error) {
      console.error('Error fetching family users:', error);
    }
  };

  const fetchMLTusers = async () => {
    try {
      const response = await axios.get(MLT_USERS);
      setMLTUsers(response.data);
    } catch (error) {
      console.error('Error fetching mlt users:', error);
    }
  };

  const handleRoleChange = (e) => {
    const role = parseInt(e.target.value, 10);
    setSelectedRoles([role]);
    setSelectedCoordinatorIds([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const newUser = {
      Id: parseInt(userId, 10),
      UserName,
      FirstName,
      LastName,
      Location,
      MarsChampion,
      UserTypes: selectedRoles,
      CoordinatorId: selectedCoordinatorIds,
      IsDisabled: isDisabled
    };

    if ([familyCoordinatorRoleId, mltCoordinatorRoleId].includes(parseInt(newUser.UserType)) && newUser.CoordinatorId.length === 0) {
      alert("You must select at least one assignee for coordinator role");
      return;
    }
    newUser.CoordinatorId = newUser.CoordinatorId.join(",");

    try {
      await axios.put(`${USERS_BASE_URL}/${userId}`, newUser);
      navigate(-1);
    } catch (error) {
      console.error('Error saving user:', error);
      alert("Error on updating user. Contact your administrator.")
    }
  };

  const handleCancel = () => {
    navigate(-1);
  };

  const getCoordinatorMappingPlaceholder = () => {
    if (selectedRoles.includes(familyCoordinatorRoleId))
      return 'Type the family member names';

    if (selectedRoles.includes(mltCoordinatorRoleId))
      return 'Type the MLT user names';

    return '';
  }

  const InfoIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="24" height="24" style={infoIconStyle}>
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" />
      <line x1="12" y1="16" x2="12" y2="12" stroke="currentColor" strokeWidth="2" />
      <circle cx="12" cy="8" r="1" fill="currentColor" />
    </svg>
  );

  const infoIconStyle = {
    color: 'darkblue',
    marginRight: '10px',
    fontSize: '16px',
    cursor: 'pointer'
  };

  const tooltipTextStyle = {
    visibility: 'hidden',
    width: '300px',
    backgroundColor: 'darkblue',
    color: '#fff',
    textAlign: 'center',
    borderRadius: '5px',
    padding: '5px',
    position: 'absolute',
    zIndex: 1,
    bottom: '125%',
    left: '50%',
    marginLeft: '-150px',
    opacity: 0,
    transition: 'opacity 0.3s'
  };

  const tooltipHoverStyle = {
    visibility: 'visible',
    opacity: 1
  };

  const toggleMarsChampion = () => {
    setMarsChampion((MarsChampion) => !MarsChampion);
  };

  const toggleIsDisabled = () => {
    setIsDisabled((isDisabled) => !isDisabled);
  };

  return (
    <>
      <Header />
      <div className="container-fluid">
        <div className='row m-2'>
          <div className='m-2 bg-light rounded p-4'>
            <div className="large-custom-font">Update User</div>
          </div>
          <div className='m-2 bg-light rounded p-0'>
            <div className="m-3">
              <form onSubmit={handleSubmit}>
                <div className="mb-3">
                  <label htmlFor="UserName" className="form-label">Username</label>
                  <input
                    type="text"
                    className="form-control"
                    id="UserName"
                    value={UserName ?? ''}
                    onChange={(e) => setUsername(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="FirstName" className="form-label">First Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="FirstName"
                    value={FirstName ?? ''}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="LastName" className="form-label">Last Name</label>
                  <input
                    type="text"
                    className="form-control"
                    id="LastName"
                    value={LastName ?? ''}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="Email" className="form-label">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    id="Email"
                    value={Email}
                    disabled
                  />
                </div>
                <div className="mb-3">
                  <label htmlFor="Location" className="form-label">Location</label>
                  <input
                    type="text"
                    className="form-control"
                    id="Location"
                    value={Location ?? ''}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>
                <div className="mb-3">
                  <div className="mb-3">
                    <div key={isDisabled} className="form-check">
                      <input
                        id="field-isDisabled"
                        type="checkbox"
                        name="isDisabled"
                        checked={isDisabled ?? false}
                        className="form-check-input"
                        onChange={toggleIsDisabled}
                      />
                      <label className="form-check-label" htmlFor={`field-isDisabled`}>Disable user</label>
                    </div>
                  </div>
                  <div className="mb-3">
                    <div key={MarsChampion} className="form-check">
                      <input
                        id="field-MarsChampion"
                        type="checkbox"
                        name="MarsChampion"
                        checked={MarsChampion ?? false}
                        className="form-check-input"
                        onChange={toggleMarsChampion}
                      />
                      <label className="form-check-label" htmlFor={`field-MarsChampion`}>Mars Champion</label>
                    </div>
                  </div>
                  <label className="form-label">Roles</label>
                  {userTypes.map((userType) => (
                    <div key={userType.id} className="form-check">
                      <input
                        disabled={isDisabled}
                        type="radio"
                        className="form-check-input"
                        id={`role-${userType.id}`}
                        name="role"
                        value={userType.id}
                        checked={selectedRoles.includes(userType.id)}
                        onChange={handleRoleChange}
                      />
                      <label className="form-check-label" htmlFor={`role-${userType.id}`}>
                        {userType.type}
                        {userType.type.toLowerCase().endsWith("coordinator") && (
                          <div
                            onMouseEnter={() => setHoveredRole(userType.id)}
                            onMouseLeave={() => setHoveredRole(null)}
                            style={{ cursor: "pointer", position: "relative", display: "inline-block", marginLeft: "10px" }}>
                            <InfoIcon />
                            {hoveredRole === userType.id && (
                              <>
                                {userType.type.toLowerCase().startsWith("family") && (
                                  <span
                                    style={{
                                      ...tooltipTextStyle,
                                      ...tooltipHoverStyle,
                                    }}
                                  >
                                    Events w.r.t primary family members will be visible and will be able to perform transactions on behalf of the same.
                                  </span>
                                )}
                                {userType.type.toLowerCase().startsWith("mlt") && (
                                  <span
                                    style={{
                                      ...tooltipTextStyle,
                                      ...tooltipHoverStyle,
                                    }}
                                  >
                                    Events w.r.t primary MLT members will be visible and will be able to perform transactions on behalf of the same.
                                  </span>
                                )}
                              </>
                            )}
                          </div>)}
                      </label>
                    </div>
                  ))}
                </div>

                {(selectedRoles.includes(familyCoordinatorRoleId) || selectedRoles.includes(mltCoordinatorRoleId)) && (
                  <>
                    <div className='mb-3'>
                      <label htmlFor="readonly-input" style={{ fontWeight: "bold", color: 'darkblue' }}>
                        Assigned Users
                      </label>
                      <Combobox
                        store={combobox}
                        onOptionSubmit={handleValueSelect}>
                        <Combobox.DropdownTarget>
                          <PillsInput onClick={() => { combobox.openDropdown(); }}>
                            <Pill.Group>
                              {values}

                              <Combobox.EventsTarget>
                                <PillsInput.Field
                                  onFocus={() => { combobox.openDropdown(); }}
                                  onBlur={() => combobox.closeDropdown()}
                                  value={search}
                                  placeholder={getCoordinatorMappingPlaceholder()}
                                  onChange={(event) => {
                                    combobox.updateSelectedOptionIndex();
                                    setSearch(event.currentTarget.value);
                                  }}
                                  onKeyDown={(event) => {
                                    if (event.key === 'Backspace' && search.length === 0 && selectedCoordinatorIds.length > 0) {
                                      event.preventDefault();
                                      handleValueRemove(selectedCoordinatorIds[selectedCoordinatorIds.length - 1]);
                                    }
                                  }}
                                />
                              </Combobox.EventsTarget>
                            </Pill.Group>
                          </PillsInput>
                        </Combobox.DropdownTarget>

                        <Combobox.Dropdown>
                          <Combobox.Options mah={200} style={{ overflowY: 'auto' }}>
                            {options.length > 0 ? options : <Combobox.Empty>Nothing found...</Combobox.Empty>}
                          </Combobox.Options>
                        </Combobox.Dropdown>
                      </Combobox>
                    </div>
                  </>
                )}
                <button type="button" className="btn btn-secondary" onClick={handleCancel} style={{ marginRight: '5px' }}>Cancel</button>
                <button type="submit" className="btn btn-primary">{userId ? 'Update' : 'Submit'}</button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default AddUser;